import { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { Theme, useMediaQuery } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { Field, useFormikContext } from 'formik';
import classNames from 'classnames/bind';
import { getApiErrorMessage } from '@api/getApiError';
import { customersSlice, useLazyFetchCustomersQuery } from '@api/Trinium/Endpoints/Customer/slice';
import { Customer } from '@api/Trinium/Endpoints/Customer/types';
import { useFetchMyProfileQuery } from '@api/Trinium/Endpoints/myProfile';
import { customerColumnDefinitions, mobileCustomerColumnDefinition } from '@components/DataGrid/Customer/columns';
import FilterableDataGrid from '@components/DataGrid/Generic/FilterableDataGrid';
import { FormikButtonDropdown } from '@forms/Components/Dropdowns/ButtonDropdown';
import { FormikComplexAutocomplete } from '@forms/Components/AutocompleteInput';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import useDebouncer from '@hooks/useDebounce';
import { store } from '@reduxStore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/** We want the grid style sheet since we are rendering a grid for the input */
import styles from '../../Components/DataGrid/RenderCell.module.css';
import btnStyles from '../Components/Dropdowns/ButtonDropdown.module.css';
const styleContext = classNames.bind({ ...styles, ...btnStyles });

interface CustomerFieldProps {
  fieldName: string;
  className?: string;
  buttonClassName?: string;
  disabled?: boolean;
  required?: boolean;
  showCreditWarning?: boolean;
  /** When true, onChange will set the Field to the customer ID and not the entire object */
  useKeyOnly?: boolean;
  /** Allow Research form to switch the TextInput to Standard variant */
  variant?: string;
  /** Additional actions to take when the input value changes beyond changing the Formik Field value */
  onChangeExtension?: (value?: unknown) => void;
}

/**
 * Form Component that renders a Button Dropdown implementation designed for selecting a Customer
 * using a Data Grid Pro in the Button Dropdown Popup Component.
 */
export default function CustomerField(props: CustomerFieldProps) {
  const { getFieldMeta, setFieldValue } = useFormikContext();
  const { value } = getFieldMeta<Customer>(props.fieldName);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const columns = useMemo(() => (!isMobile ? customerColumnDefinitions : mobileCustomerColumnDefinition), [isMobile]);

  const onGridRowSelect = useCallback(
    ({ row }: GridRowParams<Customer>) => {
      void setFieldValue(props.fieldName, row);
    },
    [setFieldValue, props.fieldName]
  );

  const getRowClassName = useCallback((row: Customer) => {
    if (row.hasCreditWrn) return styles['credit-warning'];
    return undefined;
  }, []);

  return (
    <CustomerCreditWrapper
      className={props.className}
      showCreditWarning={props.showCreditWarning}>
      <Field
        name={props.fieldName}
        label='Bill To'
        component={FormikButtonDropdown({
          ButtonContent: CustomerButtonContent,
          buttonContentProps: {
            customer: value
          },
          buttonConfig: {
            className: props.buttonClassName
          },
          PopupContent: FilterableDataGrid,
          popupContentProps: {
            columnDefinitions: columns,
            onFilterCallback: onCustomerSearch,
            toolbarContent: <CustomerWarning />,
            isRowSelectable: isCustomerRowSelectable,
            onRowSelect: onGridRowSelect,
            getRowClassName: getRowClassName
          }
        })}
        className={styleContext({
          'credit-warning': props.showCreditWarning,
          'flex-grow': true
        })}
        width='800px'
        height='280px'
        disabled={props.disabled}
        required={props.required}
        clearable
      />
    </CustomerCreditWrapper>
  );
}

/** Button Display */
function CustomerButtonContent({ customer }: { customer?: Customer | null }) {
  if (!customer) return 'Select a Customer';
  return (
    <span
      style={{
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden'
      }}>{`${customer.name} (${customer.id})`}</span>
  );
}

/** Callback when the Quick Filter value changes */
const onCustomerSearch = (searchValue: string) => {
  const terminalNumber = store.getState().applicationState.selectedTerminal?.number;

  if (!terminalNumber) throw new Error('Could not resolve a selected terminal for the application.');

  return new Promise<Customer[]>((resolve, reject) => {
    store
      .dispatch(
        customersSlice.endpoints.fetchCustomers.initiate({
          terminalNumber: terminalNumber,
          startsWith: searchValue
        })
      )
      .unwrap()
      .then((response) => {
        resolve(response);
      })
      .catch((error: unknown) => {
        const deserializedError = getApiErrorMessage(error);
        reject(new Error(deserializedError));
      });
  });
};

/** Display message about Customers who have a Credit Warning, shown in the toolbar */
function CustomerWarning() {
  const { data } = useFetchMyProfileQuery(undefined);
  return <div className={styles['customer-warning']}>{data?.settings.custCreditWrn}</div>;
}

/** Only Active Customers can be selected */
function isCustomerRowSelectable(row: Customer) {
  return row.isActive;
}

const getCustomerOptionKey = (option?: Customer) => option?.id ?? null;

const getCustomerOptionLabel = (option?: Customer) => (option ? `${option.name || '<Unmapped>'} (${option.id})` : '');

const isCustomerOptionEqual = (option?: Customer, value?: Customer) => option?.id === value?.id || option?.name === value?.name;

const renderCustomerOptions = (
  props: React.HTMLAttributes<HTMLLIElement> & {
    key: string;
  },
  value: Customer
) => {
  return (
    <li
      {...props}
      key={value.id}
      className={`${props.className ?? ''} ${styles['customer-autocomplete-list-item']}`}>
      <span
        className={styleContext({
          bold: true,
          ellipsis: true,
          name: true
        })}
        key={`${value.id}-name`}>
        {value.name}
      </span>
      <span
        className={styleContext({
          ['secondary-text']: true,
          ellipsis: true,
          addr1: true
        })}
        key={`${value.id}-addr1`}>
        {value.addr1}
      </span>
      <span
        className={styleContext({
          ['secondary-text']: true,
          ellipsis: true,
          city: true
        })}
        key={`${value.id}-city`}>
        {value.city}
      </span>
      <span
        className={styleContext({
          ['secondary-text']: true,
          ellipsis: true,
          phone: true
        })}
        key={`${value.id}-phone`}>
        {value.phone}
      </span>
    </li>
  );
};

export function CustomerAutocompleteField({ fieldName, className, ...props }: CustomerFieldProps) {
  const { number: terminalNumber } = useCurrentTerminal();

  const [fetchCustomers, { data, isFetching }] = useLazyFetchCustomersQuery();

  const [debouncedCustomerValue, setDebouncedCustomerValue] = useState<string | null>(null);

  useDebouncer(
    () => {
      if (debouncedCustomerValue && debouncedCustomerValue.length > 2) {
        void fetchCustomers({
          terminalNumber,
          startsWith: debouncedCustomerValue,
          isActive: true
        });
      }
    },
    [debouncedCustomerValue, terminalNumber],
    500
  );

  /**
   * Callback for the on input change event upon keystroke
   */
  const onKeyDown = useCallback((value: string | null) => {
    setDebouncedCustomerValue(value);
  }, []);

  return (
    <CustomerCreditWrapper
      className={className}
      showCreditWarning={props.showCreditWarning}>
      <Field
        name={fieldName}
        label='Customer'
        placeholder='Enter Customer Name'
        component={FormikComplexAutocomplete}
        className={styles['autocomplete']}
        rootClass={styles['root']}
        autoCompleteComplexItems={data ?? []}
        onInputChange={onKeyDown}
        renderOption={renderCustomerOptions}
        getOptionKey={getCustomerOptionKey}
        getOptionLabel={getCustomerOptionLabel}
        isOptionEqualToValue={isCustomerOptionEqual}
        popperClass={styles['customer-autocomplete-popper']}
        useKeyOnly={props.useKeyOnly}
        variant={props.variant}
        onChangeExtension={props.onChangeExtension}
        isLoading={isFetching}
        disabled={props.disabled}
        required={props.required}
      />
    </CustomerCreditWrapper>
  );
}

interface CreditWrapperProps extends PropsWithChildren {
  className?: string;
  showCreditWarning?: boolean;
}

function CustomerCreditWrapper(props: CreditWrapperProps) {
  const { data: user } = useFetchMyProfileQuery(undefined);

  return (
    <div
      className={styleContext({
        ['field-container']: true,
        ['customer-credit-warning']: props.showCreditWarning,
        [props.className ?? '']: Boolean(props.className)
      })}>
      {props.children}
      {props.showCreditWarning && user?.settings.custCreditWrn && (
        <div
          title={user.settings.custCreditWrn}
          className={styles['credit-warning']}>
          <WarningAmberIcon
            fontSize='inherit'
            color='error'
          />
        </div>
      )}
    </div>
  );
}
